import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, Send, CheckCircle2, AlertCircle, MessageSquare, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Slider } from '../../ui/slider';
import { Textarea } from '../../ui/textarea';
import { Progress } from '../../ui/progress';
import { Input } from '../../ui/input';
import { mockFeedbackSnippets } from '../../../data/mockData';
import { db } from '../../../lib/db';
import { formatDate } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor, getScoreLabel, getScoreColor, calculateProgress, calculateWeightedScore } from '../../../lib/calculations';
import { v4 as uuidv4 } from 'uuid';
import { useGoals } from '../../../hooks/useDatabase';
import { useAuthStore } from '../../../store/authStore';
import { Checkbox } from '../../ui/checkbox';
import AIAnalysisSummary from '../../shared/AIAnalysisSummary';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { aiService } from '../../../lib/aiService';

const scoreLabels = {
  0: 'Weak',
  1: 'Needs Improvement',
  2: 'Below Expectations',
  3: 'Meets Expectations',
  4: 'Exceeds Expectations',
  5: 'Outstanding'
};

const initialGoalState = { title: '', description: '', dueDate: '', priority: 'medium', status: 'in_progress' };
const initialFutureGoalState = { title: '', description: '', dueDate: '', priority: 'medium', status: 'not_started' };

export default function AppraisalDetail() {
  const [feedbackSnippets, setFeedbackSnippets] = useState(() => {
    try {
      const stored = localStorage.getItem('feedbackSnippets');
      if (stored) return JSON.parse(stored);
    } catch (error) {
      console.error('Failed to load feedback snippets', error);
    }
    return mockFeedbackSnippets;
  });
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { deleteGoal: deleteGoalRecord } = useGoals();
  const [activeTab, setActiveTab] = useState('performance');
  const [appraisal, setAppraisal] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [currentCompetencyIndex, setCurrentCompetencyIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [newGoal, setNewGoal] = useState(initialGoalState);
  const [newFutureGoal, setNewFutureGoal] = useState(initialFutureGoalState);
  const [myGoals, setMyGoals] = useState([]);
  const [importSelection, setImportSelection] = useState([]);
  const [importTarget, setImportTarget] = useState('current');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showFutureGoalModal, setShowFutureGoalModal] = useState(false);
  const [isImprovingAll, setIsImprovingAll] = useState(false);
  const [improvingIndex, setImprovingIndex] = useState(null);

  useEffect(() => {
    loadAppraisal();
  }, [id]);

  useEffect(() => {
    const loadMyGoals = async () => {
      if (!user) return;
      try {
        const goals = await db.goals.where('userId').equals(user.id).toArray();
        setMyGoals(goals);
      } catch (error) {
        console.error('Error loading goals for import:', error);
      }
    };
    loadMyGoals();
  }, [user]);

  const loadAppraisal = async () => {
    try {
      const data = await db.appraisals.get(id);
      if (data) {
        setAppraisal(data);
      } else {
        console.error('Appraisal not found');
        // Optional: show error or redirect
        // navigate('/employee'); 
      }
    } catch (error) {
      console.error('Error loading appraisal:', error);
    }
  };

  const recomputeProgress = (draft) => {
    const performanceAreasComplete = draft.performanceAreas?.every(area => area.employeeScore !== undefined || area.completedByEmployee);
    const goalsComplete = (draft.goals?.current?.length || 0) > 0 || (draft.goals?.future?.length || 0) > 0;
    const commentsComplete = Boolean(draft.comments?.employee);
    return {
      performanceAreasComplete,
      goalsComplete,
      commentsComplete,
      overallPercent: calculateProgress(draft)
    };
  };

  const syncGoalsToPersonal = async (draft) => {
    if (!user) return;
    const existing = await db.goals.where('appraisalId').equals(draft.id).toArray();
    const currentGoals = draft.goals?.current || [];
    const futureGoals = draft.goals?.future || [];
    const allGoals = [...currentGoals, ...futureGoals];
    const currentIds = allGoals.map(g => g.id);

    for (const goal of allGoals) {
      await db.goals.put({
        id: goal.id,
        userId: draft.userId || user.id,
        appraisalId: draft.id,
        title: goal.title,
        description: goal.description || '',
        status: goal.status || 'in_progress',
        priority: goal.priority || 'medium',
        type: goal.type || 'current',
        dueDate: goal.dueDate,
        completedDate: goal.completedDate || null,
        employeeComment: goal.employeeComment || '',
        managerComment: goal.managerComment || '',
        rolloverToNext: goal.rolloverToNext || false,
        addedBy: goal.addedBy || 'employee',
        createdAt: goal.createdAt || Date.now(),
        updatedAt: Date.now()
      });
    }

    for (const goal of existing) {
      if (!currentIds.includes(goal.id)) {
        await db.goals.delete(goal.id);
      }
    }
  };

  const toggleImportSelection = (goalId) => {
    setImportSelection(prev => prev.includes(goalId) ? prev.filter(id => id !== goalId) : [...prev, goalId]);
  };

  const handleImportGoals = async () => {
    if (!appraisal || importSelection.length === 0) return;
    const toImport = myGoals.filter(g => importSelection.includes(g.id));
    const targetList = importTarget === 'future' ? (appraisal.goals?.future || []) : (appraisal.goals?.current || []);
    const existingIds = targetList.map(g => g.id);
    const mapped = toImport
      .filter(g => !existingIds.includes(g.id))
      .map(g => ({
        id: g.id,
        title: g.title,
        description: g.description || '',
        status: g.status || 'in_progress',
        priority: g.priority || 'medium',
        type: g.type || 'current',
        dueDate: g.dueDate,
        createdAt: g.createdAt,
        completedDate: g.completedDate,
        employeeComment: g.employeeComment,
        managerComment: g.managerComment,
        rolloverToNext: g.rolloverToNext,
        addedBy: g.addedBy || 'employee'
      }));

    if (mapped.length === 0) {
      setShowImportModal(false);
      setImportSelection([]);
      return;
    }

    const updated = { ...appraisal };
    if (importTarget === 'future') {
      updated.goals = { ...updated.goals, future: [...(updated.goals?.future || []), ...mapped] };
    } else {
      updated.goals = { ...updated.goals, current: [...(updated.goals?.current || []), ...mapped] };
    }
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };

    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await syncGoalsToPersonal(updated);
      setAppraisal(updated);
      setShowImportModal(false);
      setImportSelection([]);
      setImportTarget('current');
    } catch (error) {
      console.error('Error importing goals:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updated = { ...appraisal, updatedAt: Date.now() };
      updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
      await syncGoalsToPersonal(updated);
      await db.appraisals.put(updated);
      setAppraisal(updated);
    } catch (error) {
      console.error('Error saving appraisal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      const now = Date.now();
      const shouldAutoFinalize = !appraisal?.managerId || appraisal?.visibleToSupervisor === false || appraisal?.directFinalize === true || user?.role === 'supervisor';
      const weightedScore = calculateWeightedScore(appraisal.performanceAreas || []);
      const scoresUpdate = {
        ...(appraisal.scores || {}),
        employeeSelfTotal: weightedScore || 0
      };
      if (shouldAutoFinalize) {
        scoresUpdate.finalWeightedScore = weightedScore || 0;
      }
      const updated = { 
        ...appraisal, 
        status: shouldAutoFinalize ? 'finalized' : 'employee_completed',
        updatedAt: now,
        employeeSubmittedAt: now,
        finalizedAt: shouldAutoFinalize ? now : appraisal?.finalizedAt || null,
        managerSubmittedAt: shouldAutoFinalize ? now : appraisal?.managerSubmittedAt || null,
        scores: scoresUpdate
      };

      updated.progress = { ...updated.progress, ...recomputeProgress(updated) };

      await syncGoalsToPersonal(updated);
      await db.appraisals.put(updated);
      setShowSubmitModal(false);
      navigate('/employee');
    } catch (error) {
      console.error('Error submitting appraisal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewGoalChange = (field, value) => {
    setNewGoal(prev => ({ ...prev, [field]: value }));
  };

  const handleNewFutureGoalChange = (field, value) => {
    setNewFutureGoal(prev => ({ ...prev, [field]: value }));
  };

  const addGoal = async () => {
    if (!newGoal.title.trim() || !newGoal.dueDate) return;
    const goal = {
      id: uuidv4(),
      title: newGoal.title.trim(),
      description: newGoal.description.trim(),
      status: newGoal.status,
      priority: newGoal.priority,
      dueDate: new Date(newGoal.dueDate).getTime(),
      addedBy: 'employee'
    };
    const updated = { ...appraisal };
    updated.goals = { ...updated.goals, current: [...(updated.goals?.current || []), goal] };
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await syncGoalsToPersonal(updated);
      setAppraisal(updated);
      setNewGoal(initialGoalState);
      setShowGoalModal(false);
    } catch (error) {
      console.error('Error adding goal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const addFutureGoal = async () => {
    if (!newFutureGoal.title.trim() || !newFutureGoal.dueDate) return;
    const goal = {
      id: uuidv4(),
      title: newFutureGoal.title.trim(),
      description: newFutureGoal.description.trim(),
      status: newFutureGoal.status,
      priority: newFutureGoal.priority,
      dueDate: new Date(newFutureGoal.dueDate).getTime(),
      addedBy: 'employee'
    };
    const updated = { ...appraisal };
    updated.goals = { ...updated.goals, future: [...(updated.goals?.future || []), goal] };
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await syncGoalsToPersonal(updated);
      setAppraisal(updated);
      setNewFutureGoal(initialFutureGoalState);
      setShowFutureGoalModal(false);
    } catch (error) {
      console.error('Error adding future goal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const removeGoal = async (goalId) => {
    const updated = { ...appraisal };
    updated.goals = {
      ...updated.goals,
      current: (updated.goals?.current || []).filter(g => g.id !== goalId)
    };
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await deleteGoalRecord(goalId);
      await syncGoalsToPersonal(updated);
      setAppraisal(updated);
    } catch (error) {
      console.error('Error removing goal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const removeFutureGoal = async (goalId) => {
    const updated = { ...appraisal };
    updated.goals = {
      ...updated.goals,
      future: (updated.goals?.future || []).filter(g => g.id !== goalId)
    };
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await syncGoalsToPersonal(updated);
      setAppraisal(updated);
    } catch (error) {
      console.error('Error removing future goal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!appraisal) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const handleScoreChange = (index, value) => {
    const updated = { ...appraisal };
    updated.performanceAreas = [...appraisal.performanceAreas];
    updated.performanceAreas[index] = { ...updated.performanceAreas[index] };
    updated.performanceAreas[index].employeeScore = value[0];
    updated.performanceAreas[index].completedByEmployee = true;
    setAppraisal(updated);
  };

  const handleCommentChange = (index, value) => {
    const updated = { ...appraisal };
    updated.performanceAreas = [...appraisal.performanceAreas];
    updated.performanceAreas[index] = { ...updated.performanceAreas[index] };
    updated.performanceAreas[index].employeeComment = value;
    setAppraisal(updated);
  };

  const handleOverallCommentChange = (value) => {
    const updated = { ...appraisal };
    updated.comments = { ...updated.comments, employee: value };
    setAppraisal(updated);
  };

  const insertFeedback = (text) => {
    if (currentCompetencyIndex !== null) {
      const updated = { ...appraisal };
      updated.performanceAreas = [...updated.performanceAreas];
      updated.performanceAreas[currentCompetencyIndex] = { ...updated.performanceAreas[currentCompetencyIndex] };
      const current = updated.performanceAreas[currentCompetencyIndex].employeeComment || '';
      updated.performanceAreas[currentCompetencyIndex].employeeComment = current + (current ? ' ' : '') + text;
      setAppraisal(updated);
    }
    setShowFeedbackModal(false);
  };




  const handleImproveAll = async () => {
    setIsImprovingAll(true);
    
    try {
      const updated = { ...appraisal };
      const promises = updated.performanceAreas.map(async (area) => {
        if (area.employeeComment && area.employeeComment.trim()) {
           // Skip if already improved to save tokens (simple heuristic or just let user decide)
           // For now, always improve.
           
           try {
             const corrected = await aiService.correctText(area.employeeComment);
             return {
               ...area,
               employeeComment: corrected
             };
           } catch (e) {
             console.error("Failed to correct comment:", e);
             return area;
           }
        }
        return area;
      });

      const improvedAreas = await Promise.all(promises);
      updated.performanceAreas = improvedAreas;
      
      setAppraisal(updated);
      // Optional: Save immediately?
      // await db.appraisals.put(updated); 
      toast.success("Comments enhanced by AI!");
    } catch (error) {
      console.error("Error enhancing comments:", error);
      toast.error(error.message || "Failed to enhance comments.");
    } finally {
      setIsImprovingAll(false);
    }
  };


  const calculateProgress = () => {
    const totalAreas = appraisal.performanceAreas.length;
    const completedAreas = appraisal.performanceAreas.filter(a => a.employeeScore !== undefined).length;
    return Math.round((completedAreas / totalAreas) * 100);
  };

  const canSubmit = () => {
    return appraisal.performanceAreas.every(area => {
      if (area.employeeScore === undefined) return false;
      if (area.employeeScore <= 1 || area.employeeScore >= 5) {
        return area.employeeComment && area.employeeComment.length > 0;
      }
      return true;
    });
  };

  const isReadOnly = appraisal.status === 'finalized' || appraisal.status === 'employee_completed';
  const employeeCurrentGoals = (appraisal.goals?.current || []).filter(g => (g.addedBy || 'employee') !== 'supervisor');
  const supervisorCurrentGoals = (appraisal.goals?.current || []).filter(g => (g.addedBy || 'employee') === 'supervisor');
  const employeeFutureGoals = (appraisal.goals?.future || []).filter(g => (g.addedBy || 'employee') !== 'supervisor');
  const supervisorFutureGoals = (appraisal.goals?.future || []).filter(g => (g.addedBy || 'employee') === 'supervisor');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{appraisal.title}</h1>
            <Badge className={getStatusBadgeColor(appraisal.status)}>
              {formatStatus(appraisal.status)}
            </Badge>
          </div>
          <p className="text-gray-500 mt-1">Due: {formatDate(appraisal.dueDate)}</p>
        </div>
        {!isReadOnly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSave} disabled={isSaving}>
              <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button 
              className="bg-teal-600 hover:bg-teal-700 text-white"
              disabled={!canSubmit() || isSaving}
              onClick={() => setShowSubmitModal(true)}
            >
              <Send className="w-4 h-4 mr-2" /> Submit
            </Button>
          </div>
        )}
      </div>

      <AIAnalysisSummary appraisal={appraisal} />

      {/* Progress Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Completion Progress</p>
              <p className="text-2xl font-bold text-gray-900">{calculateProgress()}%</p>
            </div>
            {appraisal.scores?.employeeSelfTotal && (
              <div className="text-right">
                <p className="text-sm font-medium text-gray-500">Self Score</p>
                <p className={`text-2xl font-bold ${getScoreColor(appraisal.scores.employeeSelfTotal)}`}>
                  {appraisal.scores.employeeSelfTotal.toFixed(1)}
                </p>
              </div>
            )}
          </div>
          <Progress value={calculateProgress()} className="h-2" />
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="performance" className="data-[state=active]:bg-white">Performance Areas</TabsTrigger>
          <TabsTrigger value="goals" className="data-[state=active]:bg-white">Goals</TabsTrigger>
          <TabsTrigger value="comments" className="data-[state=active]:bg-white">Comments</TabsTrigger>
          {appraisal.status === 'finalized' && (
            <TabsTrigger value="summary" className="data-[state=active]:bg-white">Summary</TabsTrigger>
          )}
        </TabsList>

        {/* Performance Areas Tab */}
        <TabsContent value="performance" className="space-y-4 mt-6">
          {!isReadOnly && (
            <div className="flex justify-end mb-4">
              <Button
                onClick={handleImproveAll}
                disabled={isImprovingAll}
                className={`
                  bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 
                  text-white shadow-md hover:shadow-lg transition-all duration-300
                  ${isImprovingAll ? 'opacity-80' : ''}
                `}
              >
                <Sparkles className={`w-4 h-4 mr-2 ${isImprovingAll ? 'animate-spin' : ''}`} />
                {isImprovingAll ? 'Enhancing Comments...' : 'Enhance Comments with AI'}
              </Button>
            </div>
          )}
          {appraisal.performanceAreas.map((area, index) => {
            const needsComment = (area.employeeScore !== undefined) && 
              (area.employeeScore <= 1 || area.employeeScore >= 5);
            const hasManagerResponse = area.managerScore !== undefined || (area.managerComment && area.managerComment.trim().length > 0);
            
            return (
              <Card key={area.competencyId} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">{area.competencyName}</h3>
                      <p className="text-sm text-gray-500">Weight: {area.weight}%</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {area.completedByEmployee && (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      )}
                      {area.employeeScore !== undefined && (
                        <Badge className={`text-lg px-3 py-1 ${getScoreColor(area.employeeScore)}`}>
                          {Number(area.employeeScore).toFixed(1)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Score Slider */}
                  <div className="mb-6">
                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                      {Object.entries(scoreLabels).map(([score, label]) => (
                        <span key={score} className="text-center w-16">{label}</span>
                      ))}
                    </div>
                    <Slider
                      value={[area.employeeScore ?? 3]}
                      onValueChange={(value) => handleScoreChange(index, value)}
                      max={5}
                      min={0}
                      step={0.1}
                      disabled={isReadOnly}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm font-medium mt-2">
                      {[0, 1, 2, 3, 4, 5].map(n => (
                        <span key={n} className={Math.round(area.employeeScore) === n ? 'text-teal-600' : 'text-gray-300'}>
                          {n}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Comment Section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">
                        Comments {needsComment && <span className="text-rose-500">*</span>}
                      </label>
                      {!isReadOnly && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            setCurrentCompetencyIndex(index);
                            setShowFeedbackModal(true);
                          }}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" /> Use Template
                        </Button>
                      )}

                    </div>
                    <Textarea
                      placeholder={needsComment ? 'Comment required for this score' : 'Add your comments...'}
                      value={area.employeeComment || ''}
                      onChange={(e) => handleCommentChange(index, e.target.value)}
                      disabled={isReadOnly}
                      className={needsComment && !area.employeeComment ? 'border-rose-300' : ''}
                    />
                    {needsComment && !area.employeeComment && (
                      <p className="text-xs text-rose-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Comment required for scores ≤1 or ≥5
                      </p>
                    )}
                  </div>

                  {hasManagerResponse && (
                    <div className="mt-6 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 mb-1">Manager Response</p>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Badge className={`text-xs ${getScoreColor(area.managerScore)}`}>
                          {area.managerScore !== undefined ? Number(area.managerScore).toFixed(1) : 'N/A'}
                        </Badge>
                        <span className="text-gray-500">{area.managerComment || 'No manager comment provided'}</span>
                      </div>
                    </div>
                  )}

                  {/* Manager Score (if available) */}
                  {area.managerScore !== undefined && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Manager Score</span>
                        <Badge className={`${getScoreColor(area.managerScore)}`}>
                          {area.managerScore}
                        </Badge>
                      </div>
                      {area.managerComment && (
                        <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-3 rounded-lg">
                          {area.managerComment}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Goals Tab */}
        <TabsContent value="goals" className="space-y-6 mt-6">
          {!isReadOnly && (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex gap-2 flex-wrap">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setShowGoalModal(true)} disabled={isSaving}>
                    Add Goal
                  </Button>
                  <Button className="bg-teal-600/90 hover:bg-teal-700 text-white" onClick={() => setShowFutureGoalModal(true)} disabled={isSaving}>
                    Add Future Goal
                  </Button>
                  <Button variant="outline" onClick={() => setShowImportModal(true)} disabled={isSaving}>
                    Import from My Goals
                  </Button>
                </div>
                <div className="text-sm text-gray-500">Add new or pull existing goals into this appraisal.</div>
              </div>
            </>
          )}

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Current Goals</CardTitle>
            </CardHeader>
            <CardContent>
              {(employeeCurrentGoals.length + supervisorCurrentGoals.length) > 0 ? (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Your Goals</p>
                    {employeeCurrentGoals.length > 0 ? (
                      <div className="space-y-4">
                        {employeeCurrentGoals.map(goal => (
                          <div key={goal.id} className="p-4 border border-gray-100 rounded-xl">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{goal.title}</h4>
                                <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                                <p className="text-xs text-gray-400 mt-2">Due: {formatDate(goal.dueDate)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={`${
                                  goal.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                  goal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {goal.status?.replace('_', ' ')}
                                </Badge>
                                <Badge className={`${
                                  goal.priority === 'high' ? 'bg-rose-100 text-rose-700' :
                                  goal.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {goal.priority}
                                </Badge>
                                {!isReadOnly && (
                                  <Button variant="ghost" size="sm" onClick={() => removeGoal(goal.id)}>
                                    Remove
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No goals you added</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Supervisor Goals</p>
                    {supervisorCurrentGoals.length > 0 ? (
                      <div className="space-y-4">
                        {supervisorCurrentGoals.map(goal => (
                          <div key={goal.id} className="p-4 border border-gray-100 rounded-xl">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{goal.title}</h4>
                                <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                                <p className="text-xs text-gray-400 mt-2">Due: {formatDate(goal.dueDate)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={`${
                                  goal.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                  goal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {goal.status?.replace('_', ' ')}
                                </Badge>
                                <Badge className={`${
                                  goal.priority === 'high' ? 'bg-rose-100 text-rose-700' :
                                  goal.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {goal.priority}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No supervisor goals</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No current goals</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Future Goals</CardTitle>
            </CardHeader>
            <CardContent>
              {(employeeFutureGoals.length + supervisorFutureGoals.length) > 0 ? (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Your Future Goals</p>
                    {employeeFutureGoals.length > 0 ? (
                      <div className="space-y-4">
                        {employeeFutureGoals.map(goal => (
                          <div key={goal.id} className="p-4 border border-gray-100 rounded-xl">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{goal.title}</h4>
                                <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                                <p className="text-xs text-gray-400 mt-2">Target: {formatDate(goal.dueDate)}</p>
                              </div>
                              {!isReadOnly && (
                                <Button variant="ghost" size="sm" onClick={() => removeFutureGoal(goal.id)}>
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No future goals you added</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Supervisor Future Goals</p>
                    {supervisorFutureGoals.length > 0 ? (
                      <div className="space-y-4">
                        {supervisorFutureGoals.map(goal => (
                          <div key={goal.id} className="p-4 border border-gray-100 rounded-xl">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{goal.title}</h4>
                                <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                                <p className="text-xs text-gray-400 mt-2">Target: {formatDate(goal.dueDate)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No supervisor future goals</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No future goals defined</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Import from My Goals</DialogTitle>
              <DialogDescription>Select existing goals to pull into this appraisal.</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-3 text-sm text-gray-700 mb-3">
              <span>Import to:</span>
              <select
                className="border rounded-md px-2 py-1 text-sm"
                value={importTarget}
                onChange={(e) => setImportTarget(e.target.value)}
              >
                <option value="current">Current Goals</option>
                <option value="future">Future Goals</option>
              </select>
            </div>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {myGoals.length === 0 && (
                <p className="text-sm text-gray-500">You have no saved goals to import.</p>
              )}
              {myGoals.map(goal => {
                const alreadyInAppraisal = importTarget === 'future'
                  ? (appraisal?.goals?.future || []).some(g => g.id === goal.id)
                  : (appraisal?.goals?.current || []).some(g => g.id === goal.id);
                return (
                  <div key={goal.id} className={`flex items-start gap-3 p-3 border border-gray-100 rounded-lg ${alreadyInAppraisal ? 'bg-gray-50' : ''}`}>
                    <Checkbox
                      checked={importSelection.includes(goal.id)}
                      disabled={alreadyInAppraisal}
                      onCheckedChange={() => toggleImportSelection(goal.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{goal.title}</h4>
                        <Badge className={`${
                          goal.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          goal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {goal.status?.replace('_', ' ')}
                        </Badge>
                        <Badge className={`${
                          goal.priority === 'high' ? 'bg-rose-100 text-rose-700' :
                          goal.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {goal.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{goal.description || 'No description provided.'}</p>
                      <p className="text-xs text-gray-400 mt-1">Due: {formatDate(goal.dueDate)}</p>
                      {alreadyInAppraisal && <p className="text-xs text-emerald-600 mt-1">Already included in this appraisal</p>}
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleImportGoals} disabled={importSelection.length === 0 || isSaving}>
                Import Selected
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Comments Tab */}
        <TabsContent value="comments" className="space-y-6 mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Overall Comments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Your Comments</label>
                  <Textarea
                    placeholder="Share your overall thoughts about this review period..."
                    value={appraisal.comments?.employee || ''}
                    onChange={(e) => handleOverallCommentChange(e.target.value)}
                    disabled={isReadOnly}
                    className="min-h-32"
                  />
                </div>

                {appraisal.comments?.manager && (
                  <div className="pt-4 border-t border-gray-100">
                    <label className="text-sm font-medium text-gray-700 block mb-2">Manager Comments</label>
                    <div className="bg-gray-50 p-4 rounded-lg text-gray-600">
                      {appraisal.comments.manager}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summary Tab (for finalized) */}
        {appraisal.status === 'finalized' && (
          <TabsContent value="summary" className="space-y-6 mt-6">
            <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-500 mb-2">Final Weighted Score</p>
                  <p className={`text-5xl font-bold ${getScoreColor(appraisal.scores?.finalWeightedScore)}`}>
                    {appraisal.scores?.finalWeightedScore?.toFixed(2) || 'N/A'}
                  </p>
                  <Badge className="mt-3 bg-white">
                    {getScoreLabel(appraisal.scores?.finalWeightedScore)}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="text-center p-4 bg-white/60 rounded-lg">
                    <p className="text-sm text-gray-500">Self Score</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {appraisal.scores?.employeeSelfTotal?.toFixed(2) || 'N/A'}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-white/60 rounded-lg">
                    <p className="text-sm text-gray-500">Manager Score</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {appraisal.scores?.managerTotal?.toFixed(2) || 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <GoalModal
        title="Add a Goal"
        isOpen={showGoalModal}
        onClose={() => setShowGoalModal(false)}
        goal={newGoal}
        onChange={handleNewGoalChange}
        onSave={addGoal}
        isSaving={isSaving}
        dueLabel="Due Date"
        descriptionPlaceholder="What will you accomplish?"
      />

      <GoalModal
        title="Add a Future Goal"
        isOpen={showFutureGoalModal}
        onClose={() => setShowFutureGoalModal(false)}
        goal={newFutureGoal}
        onChange={handleNewFutureGoalChange}
        onSave={addFutureGoal}
        isSaving={isSaving}
        dueLabel="Target Date"
        descriptionPlaceholder="What will you pursue next period?"
      />

      {/* Feedback Template Modal */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Feedback Templates</DialogTitle>
            <DialogDescription>Select a template to insert into your comment</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {feedbackSnippets.map(snippet => (
              <div 
                key={snippet.id}
                className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => insertFeedback(snippet.text)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`text-xs ${
                    snippet.sentiment === 'positive' ? 'bg-emerald-100 text-emerald-700' :
                    snippet.sentiment === 'improvement' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {snippet.sentiment}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">{snippet.category}</Badge>
                </div>
                <p className="text-sm text-gray-700">{snippet.text}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Submit Confirmation Modal */}
      <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Appraisal</DialogTitle>
            <DialogDescription>
              Are you sure you want to submit this appraisal? You won't be able to make changes after submission.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitModal(false)}>Cancel</Button>
            <Button 
              className="bg-teal-600 hover:bg-teal-700 text-white"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? 'Submitting...' : 'Submit Appraisal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GoalModal({ title, isOpen, onClose, goal, onChange, onSave, isSaving, dueLabel = 'Due Date', descriptionPlaceholder = 'Describe the goal' }) {
  const canSave = goal.title.trim() && goal.dueDate;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Capture the goal details before saving.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <Input
              placeholder="Goal title"
              value={goal.title}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{dueLabel}</label>
            <Input
              type="date"
              value={goal.dueDate}
              onChange={(e) => onChange('dueDate', e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Description</label>
          <Textarea
            placeholder={descriptionPlaceholder}
            value={goal.description}
            onChange={(e) => onChange('description', e.target.value)}
            className="min-h-24"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Priority</label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={goal.priority}
              onChange={(e) => onChange('priority', e.target.value)}
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={goal.status}
              onChange={(e) => onChange('status', e.target.value)}
            >
              <option value="in_progress">In Progress</option>
              <option value="not_started">Not Started</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={onSave} disabled={!canSave || isSaving}>
            {isSaving ? 'Saving...' : 'Save Goal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
